import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Alert,
} from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen } from '@/components';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';
import { useResponsive } from '@/hooks/common/useResponsive';
import { ADMIN_COLORS } from '@/constants/admin';
import { useCreateTaskMutation } from '@/store/api/admin/tasksApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useCreateTaskForm } from '@/components/admin/tasks/CreateTask/hooks/useCreateTaskForm';
import { ActionButtons, DateTimePickerModal } from '@/components';
import { TaskAssigneesSection, TaskDetailsSection, TaskSettingsSection, TaskSubtasksSection, TaskFilesSection } from '@/components/admin/tasks/CreateTask/components';
import { useDatePicker } from '@/components/admin/events/CreateEvent/hooks/useDatePicker';

type CreateTaskScreenProps = StackScreenProps<AdminStackParamList, 'CreateTask'>;

export const CreateTaskScreen: React.FC<CreateTaskScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const [createTask, { isLoading }] = useCreateTaskMutation();
  // Web-only: clear ?form=true from URL so returning to Tasks does not re-open
  const clearFormQueryParam = useCallback(() => {
    if (Platform.OS !== 'web') return;
    try {
      const loc: any = (globalThis as any).location;
      const hist: any = (globalThis as any).history;
      if (!loc || !hist) return;
      const usp = new URLSearchParams(loc.search || '');
      usp.delete('form');
      const next = usp.toString() ? `${loc.pathname}?${usp.toString()}` : loc.pathname;
      hist.replaceState(null, '', next);
    } catch {}
  }, []);

  useEffect(() => {
    clearFormQueryParam();
  }, [clearFormQueryParam]);


  // Custom hooks for form and date picker logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
  } = useCreateTaskForm();

  const {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showStartDatePicker,
    hideDatePickers,
    confirmDateSelection,
    setDatePickerMode,
    showFallbackDatePicker,
  } = useDatePicker();

  // Date selection handler
  const handleDateSelection = useCallback((date: Date | null) => {
    handleFieldChange('dueDate', date);
  }, [handleFieldChange]);

  // Due date picker handler with fallback support
  const [webAnchor, setWebAnchor] = useState<any | null>(null);

  const handleDueDatePress = useCallback((anchor?: any) => {
    const currentDate = formData.dueDate ? new Date(formData.dueDate) : new Date();
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showStartDatePicker(currentDate);
      return;
    }
    if (DateTimePicker) {
      showStartDatePicker(currentDate);
    } else {
      showFallbackDatePicker('Due Date', currentDate, (date: Date) => {
        handleDateSelection(date);
      });
    }
  }, [DateTimePicker, formData.dueDate, showStartDatePicker, showFallbackDatePicker, handleDateSelection]);

  // Handle date picker confirmation
  const handleDatePickerConfirmation = useCallback((pickerType: 'start' | 'end' | 'publish') => {
    confirmDateSelection(pickerType, (date: Date) => {
      handleDateSelection(date);
    });
  }, [confirmDateSelection, handleDateSelection]);

  // Clear due date handler
  const handleClearDueDate = useCallback(() => {
    handleFieldChange('dueDate', null);
  }, [handleFieldChange]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId) {
      showApiError('No property selected');
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      
      await createTask({
        taskForm: formData,
        selectedPropertyId,
      }).unwrap();

      showSuccess('Task created successfully!');
      clearFormQueryParam();
      navigation.goBack();
    } catch (error: any) {
      console.error('Failed to create task:', error);
      showApiError(error?.data?.message || 'Failed to create task');
    }
  }, [selectedPropertyId, formData, validateForm, createTask, showSuccess, showApiError, navigation, clearFormQueryParam]);

  // Navigation handlers
  const handleCancel = useCallback(() => {
    if (Platform.OS === 'web') {
      // Desktop/web confirmation is handled by DesktopFormPage
      clearFormQueryParam();
      navigation.goBack();
      return;
    }
    // Mobile: always confirm, even without edits
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation, clearFormQueryParam]);


  const scrollContent = useMemo(() => (
    <>
      <TaskDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
      <TaskSettingsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onDueDatePress={handleDueDatePress}
        onClearDueDate={handleClearDueDate}
      />
      <TaskAssigneesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
      <TaskSubtasksSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
      <TaskFilesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
    </>
  ), [formData, errors, handleFieldChange, handleDueDatePress, handleClearDueDate]);

  if (layoutMode === 'desktop') {
    return (
      <>
        <DesktopFormPage
          title="Create Task"
          subtitle="Add task details"
          isSubmitting={isLoading}
          submitText="Create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          confirmDiscardOnWeb={true}
        >
          {scrollContent}
        </DesktopFormPage>

        <DateTimePickerModal
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirmation}
          onCancel={hideDatePickers}
          pickerType="start"
          title="Due Date"
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
          dateOnly={true}
        />
      </>
    );
  }

  return (
    <>
      <MobileFormScreen
        screenName="CreateTask"
        navigation={navigation}
        isSubmitting={isLoading}
        submitText="Create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      >
        {scrollContent}
      </MobileFormScreen>

      <DateTimePickerModal
        DateTimePicker={DateTimePicker}
        datePickerState={datePickerState}
        onDateChange={handleDatePickerChange}
        onConfirm={handleDatePickerConfirmation}
        onCancel={hideDatePickers}
        pickerType="start"
        title="Due Date"
        onModeChange={setDatePickerMode}
        dateOnly={true}
      />
    </>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 20,
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100, // Space for action buttons
  },

});
